class LayeredImageAndText {
  constructor(container) {
    this.container = container;
    this.setupStyles();
    this.setupAnimations();
    this.setupMobileLayout();
  }

  setupStyles() {
    // Set desktop styles
    const desktopStyles = {
      '--height': this.container.dataset.height + 'px',
      '--min-height': this.container.dataset.minHeight + 'px',
      '--padding-top': this.container.dataset.paddingTop + 'px',
      '--padding-bottom': this.container.dataset.paddingBottom + 'px',
      '--padding-left': this.container.dataset.paddingLeft + 'px',
      '--padding-right': this.container.dataset.paddingRight + 'px',
      '--grid-columns': this.container.dataset.gridColumns,
      '--grid-rows': this.container.dataset.gridRows,
      '--desktop-grid-gap': this.container.dataset.desktopGridGap + 'px'
    };

    // Set mobile styles
    const mobileStyles = {
      '--mobile-height': this.container.dataset.mobileHeight + 'px',
      '--mobile-min-height': this.container.dataset.mobileMinHeight + 'px',
      '--mobile-padding-top': this.container.dataset.mobilePaddingTop + 'px',
      '--mobile-padding-bottom': this.container.dataset.mobilePaddingBottom + 'px',
      '--mobile-padding-left': this.container.dataset.mobilePaddingLeft + 'px',
      '--mobile-padding-right': this.container.dataset.mobilePaddingRight + 'px',
      '--mobile-grid-columns': this.container.dataset.mobileGridColumns,
      '--mobile-grid-rows': this.container.dataset.mobileGridRows,
      '--mobile-grid-gap': this.container.dataset.mobileGridGap + 'px'
    };

    // Apply desktop styles
    Object.entries(desktopStyles).forEach(([property, value]) => {
      if (value && value !== 'undefinedpx' && value !== 'undefined') {
        this.container.style.setProperty(property, value);
      }
    });

    // Apply mobile styles
    Object.entries(mobileStyles).forEach(([property, value]) => {
      if (value && value !== 'undefinedpx' && value !== 'undefined') {
        this.container.style.setProperty(property, value);
      }
    });

    // Set block styles
    this.setupBlockStyles();
  }

  setupBlockStyles() {
    // Setup text blocks
    this.container.querySelectorAll('.text-block').forEach(block => {
      
      const desktopStyles = {
        '--col-start': block.dataset.colStart,
        '--col-span': block.dataset.colSpan,
        '--row-start': block.dataset.rowStart,
        '--row-span': block.dataset.rowSpan,
        '--width': block.dataset.width + (block.dataset.widthUnit || '%'),
        '--padding-top': block.dataset.paddingTop + 'px',
        '--padding-right': block.dataset.paddingRight + 'px',
        '--padding-bottom': block.dataset.paddingBottom + 'px',
        '--padding-left': block.dataset.paddingLeft + 'px',
        '--text-align': block.dataset.textAlign,
        '--font-size': block.dataset.fontSize + 'px'
      };

      const mobileStyles = {
        '--mobile-width': block.dataset.mobileWidth + (block.dataset.mobileWidthUnit || '%'),
        '--mobile-font-size': block.dataset.mobileFontSize + 'px',
        '--mobile-text-align': block.dataset.mobileTextAlign,
        '--mobile-padding-top': block.dataset.mobilePaddingTop + 'px',
        '--mobile-padding-bottom': block.dataset.mobilePaddingBottom + 'px'
      };

      Object.entries({ ...desktopStyles, ...mobileStyles }).forEach(([property, value]) => {
        if (value && value !== 'undefinedpx' && value !== 'undefined') {
          block.style.setProperty(property, value);
        }
      });
    });

    // Setup image blocks
    this.container.querySelectorAll('.image-block').forEach(block => {
      const desktopStyles = {
        '--col-start': block.dataset.colStart,
        '--col-span': block.dataset.colSpan,
        '--row-start': block.dataset.rowStart,
        '--row-span': block.dataset.rowSpan,
        '--width': block.dataset.width + (block.dataset.widthUnit || '%'),
        '--padding-top': block.dataset.paddingTop + 'px',
        '--padding-right': block.dataset.paddingRight + 'px',
        '--padding-bottom': block.dataset.paddingBottom + 'px',
        '--padding-left': block.dataset.paddingLeft + 'px',
        '--z-index': block.dataset.zIndex,
        '--offset-x': block.dataset.offsetX + 'px',
        '--offset-y': block.dataset.offsetY + 'px',
        '--horizontal-align': block.dataset.horizontalAlign,
        '--image-height': block.dataset.imageHeight + 'px',
        '--aspect-ratio': this.getAspectRatioValue(block.dataset.aspectRatio)
      };

      const mobileStyles = {
        '--mobile-width': block.dataset.mobileWidth + (block.dataset.mobileWidthUnit || '%'),
        '--mobile-padding-top': block.dataset.mobilePaddingTop + 'px',
        '--mobile-padding-bottom': block.dataset.mobilePaddingBottom + 'px',
        '--mobile-image-height': block.dataset.mobileImageHeight + 'px',
        '--mobile-aspect-ratio': this.getAspectRatioValue(block.dataset.mobileAspectRatio),
        '--mobile-image-height-type': block.dataset.mobileImageHeightType
      };

      Object.entries({ ...desktopStyles, ...mobileStyles }).forEach(([property, value]) => {
        if (value && value !== 'undefinedpx' && value !== 'undefined') {
          block.style.setProperty(property, value);
        }
      });
    });
  }

  getAspectRatioValue(ratio) {
    if (!ratio) return '16/9';
    const [width, height] = ratio.split(':');
    return `${width}/${height}`;
  }

  setupAnimations() {
    const animation = this.container.dataset.animation;
    const animationDelay = parseInt(this.container.dataset.animationDelay) || 0;
    const mobileAnimation = this.container.dataset.mobileAnimation;
    const mobileAnimationDelay = parseInt(this.container.dataset.mobileAnimationDelay) || 0;

    if (animation || mobileAnimation) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const isMobile = window.innerWidth <= 749;
            const currentAnimation = isMobile ? mobileAnimation : animation;
            const currentDelay = isMobile ? mobileAnimationDelay : animationDelay;

            if (currentAnimation && currentAnimation !== 'none') {
              setTimeout(() => {
                this.container.classList.add('animate');
              }, currentDelay);
            }
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      observer.observe(this.container);
    }
  }

  setupMobileLayout() {
    const layoutType = this.container.dataset.mobileLayoutType;
    
    if (layoutType === 'carousel') {
      this.setupCarousel();
    } else if (layoutType === 'masonry') {
      this.setupMasonry();
    }
  }

  setupCarousel() {
    const blocks = this.container.querySelectorAll('.flex-block');
    let currentIndex = 0;
    let startX = 0;
    let scrollLeft = 0;

    // Add touch event listeners for swipe
    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].pageX - this.container.offsetLeft;
      scrollLeft = this.container.scrollLeft;
    });

    this.container.addEventListener('touchmove', (e) => {
      if (!e.touches[0]) return;
      const x = e.touches[0].pageX - this.container.offsetLeft;
      const walk = (x - startX) * 2;
      this.container.scrollLeft = scrollLeft - walk;
    });

    // Optional: Add navigation dots
    if (blocks.length > 1) {
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'carousel-dots';
      
      blocks.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'carousel-dot' + (index === 0 ? ' active' : '');
        dot.addEventListener('click', () => {
          this.scrollToSlide(index);
        });
        dotsContainer.appendChild(dot);
      });

      this.container.parentNode.insertBefore(dotsContainer, this.container.nextSibling);

      // Update dots on scroll
      this.container.addEventListener('scroll', () => {
        const scrollPosition = this.container.scrollLeft;
        const slideWidth = this.container.offsetWidth;
        currentIndex = Math.round(scrollPosition / slideWidth);
        
        dotsContainer.querySelectorAll('.carousel-dot').forEach((dot, index) => {
          dot.classList.toggle('active', index === currentIndex);
        });
      });
    }
  }

  scrollToSlide(index) {
    const slideWidth = this.container.offsetWidth;
    this.container.scrollTo({
      left: index * slideWidth,
      behavior: 'smooth'
    });
  }

  setupMasonry() {
    const blocks = this.container.querySelectorAll('.flex-block');
    const columns = parseInt(this.container.dataset.mobileColumns) || 1;
    
    // Reset any existing styles
    blocks.forEach(block => {
      block.style.gridColumn = '';
      block.style.gridRow = '';
    });

    // Simple masonry layout
    if (columns > 1) {
      let columnHeights = new Array(columns).fill(0);
      let columnIndex = 0;

      blocks.forEach(block => {
        // Find the shortest column
        columnIndex = columnHeights.indexOf(Math.min(...columnHeights));
        
        // Place the block in that column
        block.style.gridColumn = `${columnIndex + 1}`;
        
        // Update column height
        columnHeights[columnIndex] += block.offsetHeight;
      });
    }

    // Update layout on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth <= 749) {
          this.setupMasonry();
        }
      }, 250);
    });
  }
}

// Initialize all sections
document.querySelectorAll('.layered-image-and-text-flex').forEach(section => {
  new LayeredImageAndText(section);
}); 